import { useEffect, useRef } from 'react';

const VERTEX_SHADER = `
attribute vec2 a_position;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const FRAGMENT_SHADER = `
precision highp float;
uniform float u_time;
uniform vec2 u_resolution;
uniform vec2 u_mouse;

vec3 mod289v3(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289v2(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289v3(((x * 34.0) + 1.0) * x); }

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod289v2(i);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
  m = m * m; m = m * m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

mat2 rotate2D(float r) {
  return mat2(cos(r), sin(r), -sin(r), cos(r));
}

float dfLine(vec2 start, vec2 end, vec2 uv) {
  return length(end - start) - length(uv);
}

float circle(vec2 uv, vec2 pos, float r) {
  return smoothstep(0.0, 0.005, abs(length(uv - pos) - r));
}

vec3 palette(float t) {
  vec3 a = vec3(0.5, 0.5, 0.5);
  vec3 b = vec3(0.5, 0.5, 0.5);
  vec3 c = vec3(1.0, 1.0, 1.0);
  vec3 d = vec3(0.06, 0.1, 0.18);
  return a + b * cos(6.28318 * (c * t + d));
}

vec2 distort(vec2 p, float gain, float rise) {
  float t = u_time * 0.2;
  float mouseInfluence = 0.0;
  if (u_mouse.x > 0.0) {
    vec2 mUV = u_mouse / u_resolution;
    float mX = mUV.x * 2.0 - 1.0;
    float mY = 1.0 - mUV.y;
    mouseInfluence = exp(-pow(p.x - mX, 2.0) * 2.0) * mY;
  }
  float y = p.y + gain * 0.1 * ((sin(p.x * 3.5 + t * 0.5) * 0.5 + 0.5) + mouseInfluence * 2.0) + rise;
  vec2 q = vec2(cos(t * 0.15) * p.x + sin(t * 0.25) * y + 1.0, -sin(t * 0.2) * p.x + cos(t * 0.3) * y + 1.0);
  float s = 2.5;
  float a = 0.0;
  a += snoise(q * s) / s; s *= 2.0;
  a += snoise(q * s) / s; s *= 2.0;
  a += snoise(q * s) / s; s *= 2.0;
  a += snoise(q * s) / s;
  return vec2(p.x, p.y + gain * a + rise);
}

float line(vec2 p, vec2 a, vec2 b, float w) {
  return 1.0 - smoothstep(0.0, w, abs((b.y - a.y) * p.x - (b.x - a.x) * p.y + b.x * a.y - b.y * a.x) / distance(a, b));
}

float loopLine(vec2 uv, float x, float offset) {
  vec2 p = vec2(x, -0.8);
  vec2 s = vec2(x + sin(u_time * 0.2 + offset) * 0.05, 0.8);
  vec2 d = distort(p, 0.6, 0.3);
  vec2 d2 = distort(s, 0.6, 0.3);
  return line(uv, d, d2, 0.004);
}

void main() {
  vec2 uv = (gl_FragCoord.xy - u_resolution.xy * 0.5) / min(u_resolution.x, u_resolution.y);
  vec3 col = vec3(0.06, 0.08, 0.15);
  for (float i = 0.0; i < 4.0; i++) {
    float fi = i / 4.0;
    float offset = fi * 2.0 * 3.14159;
    float l = loopLine(uv, -0.6 + fi * 0.4, offset);
    vec3 c = palette(fi + u_time * 0.05);
    col = mix(col, c, l * 0.8);
  }
  col += vec3(0.2, 0.3, 0.5) * (loopLine(uv, -0.6, 0.0) + loopLine(uv, -0.2, 2.0) + loopLine(uv, 0.2, 4.0)) * 0.1;
  col *= 0.5 + 0.5 * pow(16.0 * uv.x * uv.y * (1.0 - uv.x) * (1.0 - uv.y), 0.3);
  gl_FragColor = vec4(col, 1.0);
}
`;

export default function HeroWave() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl', { alpha: false, antialias: false });
    if (!gl) return;

    gl.clearColor(0.06, 0.08, 0.15, 1.0);
    gl.enable(gl.DEPTH_TEST);

    // Compile shader
    function compileShader(src: string, type: number) {
      const shader = gl!.createShader(type)!;
      gl!.shaderSource(shader, src);
      gl!.compileShader(shader);
      return shader;
    }

    const vs = compileShader(VERTEX_SHADER, gl.VERTEX_SHADER);
    const fs = compileShader(FRAGMENT_SHADER, gl.FRAGMENT_SHADER);

    const program = gl.createProgram()!;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.useProgram(program);

    // Geometry
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);

    const aPos = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    // Uniforms
    const uTime = gl.getUniformLocation(program, 'u_time');
    const uResolution = gl.getUniformLocation(program, 'u_resolution');
    const uMouse = gl.getUniformLocation(program, 'u_mouse');

    let mouseX = 0;
    let mouseY = 0;

    const handleMouse = (e: MouseEvent) => {
      mouseY = e.clientY;
      mouseX = e.clientX;
    };
    window.addEventListener('mousemove', handleMouse);

    let animId = 0;

    function render(time: number) {
      if (!canvas || !gl) return;
      canvas.width = window.innerWidth * 0.5;
      canvas.height = window.innerHeight * 0.5;
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform1f(uTime, time * 0.001);
      gl.uniform2f(uResolution, canvas.width, canvas.height);
      gl.uniform2f(uMouse, mouseX * 0.5, (canvas.height - mouseY) * 0.5);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      animId = requestAnimationFrame(render);
    }

    animId = requestAnimationFrame(render);

    return () => {
      window.removeEventListener('mousemove', handleMouse);
      cancelAnimationFrame(animId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
      }}
    />
  );
}
